import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {Quiz} from "types/Quiz";
import {useNotifications} from "context/NotificationContext";
import Button from "components/Button";
import {PencilIcon, TrashIcon} from "@heroicons/react/solid";
import Page from "components/Page";
import Card from "components/Card";
import DescriptionList from "components/DescriptionList";
import DescriptionListItem from "components/DescriptionListItem";
import DescriptionTerm from "components/DescriptionTerm";
import DescriptionDetails from "components/DescriptionDetails";
import DeleteQuiz from "pages/Quiz/Delete";
import Chip from 'components/Chip';
import {toTitleCase} from 'services/string';
import {CheckCircleIcon, XCircleIcon} from "@heroicons/react/outline";
import {useOperator} from "../../../context/OperatorContext";

function letterAtIndex(index: number): string {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

  return alphabet[index % alphabet.length];
}

function ViewQuiz() {
  const {termFor} = useOperator();
  const {id} = useParams()
  const {data: quiz, isLoading} = useQuery(
    ['quizzes', {id}],
    () => ngage.quizzes.get(parseInt(id as string))
  )

  const [deletingQuiz, setDeletingQuiz] = useState<Quiz | null>(null);
  const {notify} = useNotifications()
  const navigate = useNavigate()

  // Prepare action handlers
  function handleDeleteQuiz() {
    if (quiz) setDeletingQuiz(quiz)
  }

  function handleEditQuiz() {
    if (id) navigate(`/quizzes/${id}/edit`)
  }

  // Prepare page actions
  const actions = <>
    <Button color={Button.colors.WHITE} icon={TrashIcon} onClick={handleDeleteQuiz}>Delete</Button>
    <Button color={Button.colors.PRIMARY} icon={PencilIcon} onClick={handleEditQuiz}>Edit</Button>
  </>

  return (
    <Page
      title={quiz?.title || "View Quiz"}
      breadcrumbs={{"Education": "/", "Quizzes": "/quizzes", [quiz?.title || "View Quiz"]: `/quizzes/${id}`}}
      actions={actions}
      isLoading={isLoading}
    >
      {quiz ?
        <>
          {/* Quiz Information */}
          <section className="flex flex-col lg:flex-row-reverse lg:justify-end">
            {quiz.image ?
              <div
                className="flex justify-center items-center max-h-36 lg:max-h-full shadow overflow-hidden sm:rounded-md sm:mb-2 lg:mb-0 lg:ml-4">
                <img
                  src={quiz.image?.preview_url ?? quiz.image?.original_url}
                  alt={quiz.title}
                  className="object-cover w-full lg:h-full lg:w-auto"
                />
              </div>
              : null}

            <div className="lg:basis-2/3 lg:flex-shrink-0 lg:flex-grow">
              <Card
                heading="Quiz Information"
                subheading={`${quiz.question_count} questions`}
              >
                <DescriptionList>
                  <DescriptionListItem span={2}>
                    <DescriptionTerm>Description</DescriptionTerm>
                    <DescriptionDetails>
                      <p
                        className="whitespace-pre-line">{quiz.description ?? `A quiz made up of ${quiz.question_count} questions`}</p>
                    </DescriptionDetails>
                  </DescriptionListItem>

                  <DescriptionListItem span={1}>
                    <DescriptionTerm>Max Attempts</DescriptionTerm>
                    <DescriptionDetails>
                      {quiz.maximum_attempts || 'Unlimited'}
                    </DescriptionDetails>
                  </DescriptionListItem>

                  <DescriptionListItem span={1}>
                    <DescriptionTerm>Attached {termFor('Exhibit')}</DescriptionTerm>
                    <DescriptionDetails>
                      {quiz.quizzable && quiz.quizzable_type ?
                        <Chip
                          name={quiz.quizzable.name}
                          description={toTitleCase(quiz.quizzable_type)}
                          backgroundSrc={'media' in quiz.quizzable ? quiz.quizzable?.media?.[0]?.preview_url ?? quiz.quizzable?.media?.[0]?.original_url : null}
                          href={`/${quiz.quizzable_type}s/${quiz.quizzable.id}`}
                        />
                        : 'None'}
                    </DescriptionDetails>
                  </DescriptionListItem>
                </DescriptionList>
              </Card>
            </div>
          </section>

          {/* Questions */}
          <section className="mt-4">
            <ul>
              {quiz.questions.map((question, questionIndex) => (
                <li key={questionIndex} className="mb-4">
                  <Card heading={question.text} subheading={`Question ${questionIndex + 1}`}>
                    <ul className="grid md:grid-cols-2 gap-x-8 gap-y-4">
                      {question.choices.map((choice, choiceIndex) => (
                        <li key={choiceIndex}
                            className="flex justify-between items-center bg-gray-50 p-2 px-4 rounded-md">
                          <div className="flex items-center">
                            <span className="font-semibold mr-2">{letterAtIndex(choiceIndex)}</span>
                            <span className="text-sm">{choice.text}</span>
                          </div>

                          {choice.is_correct ?
                            <CheckCircleIcon className="text-green-600 h-5 w-5"/> :
                            <XCircleIcon className="text-red-600 h-5 w-5"/>
                          }
                        </li>
                      ))}
                    </ul>
                  </Card>
                </li>
              ))}
            </ul>
          </section>
        </>
        : null}

      {/* Delete confirmation modal */}
      <DeleteQuiz
        show={quiz === deletingQuiz}
        quiz={deletingQuiz}
        onCancel={() => setDeletingQuiz(null)}
        onDelete={quiz => {
          navigate('/quizzes')
          notify("success", "Quiz Deleted", `${quiz.title} has been permanently removed`)
        }}
        onDeleteFail={quiz => {
          setDeletingQuiz(null)
          notify("error", "Quiz Not Deleted", `${quiz.title} could not be removed`)
        }}
      />
    </Page>
  );
}

export default ViewQuiz;